"use client";

import { useEffect, useRef, useState } from "react";
import { useToolUi } from "@/components/tools/tool-ui";
import { parseIntegerInput } from "@/lib/parse-number";

const PRESET_MINUTES = [1, 5, 10, 15, 30, 60];

function playBeepSequence() {
  const ctx = new AudioContext();
  const beepCount = 3;
  for (let i = 0; i < beepCount; i += 1) {
    const oscillator = ctx.createOscillator();
    const gain = ctx.createGain();
    const startAt = ctx.currentTime + i * 0.45;
    oscillator.type = "sine";
    oscillator.frequency.value = 880;
    gain.gain.setValueAtTime(0.0001, startAt);
    gain.gain.exponentialRampToValueAtTime(0.3, startAt + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, startAt + 0.35);
    oscillator.connect(gain);
    gain.connect(ctx.destination);
    oscillator.start(startAt);
    oscillator.stop(startAt + 0.35);
    if (i === beepCount - 1) {
      oscillator.onended = () => {
        ctx.close();
      };
    }
  }
}

function formatTime(totalSeconds: number): string {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  const mmss = `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
  if (hours > 0) {
    return `${String(hours).padStart(2, "0")}:${mmss}`;
  }
  return mmss;
}

function clampUnit(raw: string, max: number): number {
  const parsed = parseIntegerInput(raw);
  if (parsed === null) {
    return 0;
  }
  return Math.min(max, Math.max(0, parsed));
}

function UnitInput({
  id,
  label,
  max,
  value,
  onChange,
}: {
  id: string;
  label: string;
  max: number;
  value: number;
  onChange: (value: number) => void;
}) {
  return (
    <label
      className="flex items-center justify-between gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm"
      htmlFor={id}
    >
      {label}
      <input
        className="w-16 rounded border border-border bg-background px-2 py-1 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
        id={id}
        max={max}
        min={0}
        onChange={(e) => onChange(clampUnit(e.target.value, max))}
        type="number"
        value={value}
      />
    </label>
  );
}

/** English strings. A translated content file overrides these through `ui`. */
export const COUNTDOWN_TIMER_STRINGS = {
  defaultTitle: "Countdown Timer - Free Online Timer with Sound Alert",
  timesUp: "Time's up!",
  countdownTimerTitleSuffix: " — Countdown Timer",
  pause: "Pause",
  start: "Start",
  reset: "Reset",
  setDuration: "Set duration",
  hours: "Hours",
  minutes: "Minutes",
  seconds: "Seconds",
  minSuffix: " min",
} as const;

export default function CountdownTimerWidget() {
  const [hours, setHours] = useState(0);
  const [minutes, setMinutes] = useState(5);
  const [seconds, setSeconds] = useState(0);
  const [secondsLeft, setSecondsLeft] = useState(5 * 60);
  const [running, setRunning] = useState(false);
  const [finished, setFinished] = useState(false);
  const endAtRef = useRef<number | null>(null);

  const strings = useToolUi(COUNTDOWN_TIMER_STRINGS);

  const inputTotal = hours * 3600 + minutes * 60 + seconds;

  useEffect(() => {
    if (!running) {
      return;
    }
    const id = setInterval(() => {
      const endAt = endAtRef.current;
      if (endAt === null) {
        return;
      }
      const remaining = Math.max(0, Math.round((endAt - Date.now()) / 1000));
      setSecondsLeft(remaining);
      if (remaining === 0) {
        playBeepSequence();
        setRunning(false);
        setFinished(true);
        endAtRef.current = null;
      }
    }, 250);
    return () => clearInterval(id);
  }, [running]);

  useEffect(() => {
    if (!(running || finished)) {
      document.title = strings.defaultTitle;
      return;
    }
    document.title = finished
      ? `${strings.timesUp}${strings.countdownTimerTitleSuffix}`
      : `${formatTime(secondsLeft)}${strings.countdownTimerTitleSuffix}`;
    return () => {
      document.title = strings.defaultTitle;
    };
  }, [secondsLeft, running, finished, strings]);

  function applyDuration(total: number) {
    setRunning(false);
    setFinished(false);
    endAtRef.current = null;
    setSecondsLeft(total);
  }

  function handlePreset(presetMinutes: number) {
    setHours(Math.floor(presetMinutes / 60));
    setMinutes(presetMinutes % 60);
    setSeconds(0);
    applyDuration(presetMinutes * 60);
  }

  function handleHoursChange(value: number) {
    setHours(value);
    if (!running) {
      applyDuration(value * 3600 + minutes * 60 + seconds);
    }
  }

  function handleMinutesChange(value: number) {
    setMinutes(value);
    if (!running) {
      applyDuration(hours * 3600 + value * 60 + seconds);
    }
  }

  function handleSecondsChange(value: number) {
    setSeconds(value);
    if (!running) {
      applyDuration(hours * 3600 + minutes * 60 + value);
    }
  }

  function handleStartPause() {
    if (running) {
      setRunning(false);
      endAtRef.current = null;
      return;
    }
    const total = secondsLeft > 0 ? secondsLeft : inputTotal;
    if (total === 0) {
      return;
    }
    setFinished(false);
    setSecondsLeft(total);
    endAtRef.current = Date.now() + total * 1000;
    setRunning(true);
  }

  function handleReset() {
    applyDuration(inputTotal);
  }

  const displayClass = finished
    ? "animate-pulse text-primary"
    : "text-foreground";

  return (
    <>
      {/* Timer display */}
      <section className="rounded-lg border border-border bg-card p-8 text-center">
        <p
          className={`font-mono font-semibold text-7xl tabular-nums md:text-8xl ${displayClass}`}
        >
          {formatTime(secondsLeft)}
        </p>
        {finished && (
          <p className="mt-3 font-medium text-primary text-sm">
            {strings.timesUp}
          </p>
        )}
        <div className="mt-6 flex flex-wrap justify-center gap-3">
          <button
            className="rounded-lg bg-primary px-6 py-2.5 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
            disabled={!running && secondsLeft === 0 && inputTotal === 0}
            onClick={handleStartPause}
            type="button"
          >
            {running ? strings.pause : strings.start}
          </button>
          <button
            className="rounded-lg border border-border bg-card px-6 py-2.5 font-medium text-foreground text-sm transition-colors hover:bg-muted"
            onClick={handleReset}
            type="button"
          >
            {strings.reset}
          </button>
        </div>
      </section>

      {/* Duration inputs */}
      <section className="space-y-3">
        <p className="font-medium text-foreground text-sm">
          {strings.setDuration}
        </p>
        <div className="grid gap-3 sm:grid-cols-3">
          <UnitInput
            id="cd-hours"
            label={strings.hours}
            max={23}
            onChange={handleHoursChange}
            value={hours}
          />
          <UnitInput
            id="cd-minutes"
            label={strings.minutes}
            max={59}
            onChange={handleMinutesChange}
            value={minutes}
          />
          <UnitInput
            id="cd-seconds"
            label={strings.seconds}
            max={59}
            onChange={handleSecondsChange}
            value={seconds}
          />
        </div>
        <div className="flex flex-wrap gap-2">
          {PRESET_MINUTES.map((preset) => (
            <button
              className="rounded-lg border border-border bg-card px-4 py-1.5 font-medium text-foreground text-sm transition-colors hover:bg-muted"
              key={preset}
              onClick={() => handlePreset(preset)}
              type="button"
            >
              {preset}
              {strings.minSuffix}
            </button>
          ))}
        </div>
      </section>
    </>
  );
}
